import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { useAction } from "../../../components/ui";
import { toast } from "../../../components/Toast";
import { currentLang, t } from "../../../lib/sprache";
import { saveWhatsappMode } from "./data";
import { NurAdmin, Confirm, SettingsFrame } from "./parts";
import { TarifHinweis } from "../../../components/Tarif";
import { disconnectWhatsapp, loadWhatsapp, setWhatsappEvent } from "./whatsapp-data";
import { vorlagenDetail, vorlagenKategorie, vorlagenStand, whatsappEventLabel, whatsappFuss, whatsappKnopf, whatsappVorschau, type WhatsappOverview } from "./whatsapp-model";
import { runEmbeddedSignup, whatsappSignupConfig } from "./whatsapp-signup";

/**
 * Einstellungen · WhatsApp (0159, Bauplan 1.11): eigene Nummer der Agentur, Modus und je Ereignis, ob eine WhatsApp-Nachricht
 * rausgeht. Solange der Versand nicht eingerichtet ist (whatsapp_sender_ready), steht „In Kürze verfügbar“; einstellen lässt
 * sich trotzdem alles, der Probelauf protokolliert im Postausgang, was verschickt würde.
 */
export function Whatsapp() {
  const ws = useStaffWorkspace();
  if (!ws.isChef) return <NurAdmin />;
  return <WhatsappSeite orgId={ws.org.org_id} orgName={ws.org.name} />;
}

function WhatsappSeite({ orgId, orgName }: { orgId: string; orgName: string }) {
  const q = useQuery({ queryKey: ["org_whatsapp_overview", orgId], queryFn: () => loadWhatsapp(api, orgId) });
  const text = t("Hinweise an eure Creator per WhatsApp, von eurer eigenen Nummer und nur zu den Ereignissen, die ihr wählt. Mails und Glocke bleiben wie gewohnt; WhatsApp kommt dazu.",
    "Notices to your creators via WhatsApp, from your own number and only for the events you choose. Emails and the bell stay as usual; WhatsApp comes on top.");
  return (
    <SettingsFrame section="whatsapp" orgId={orgId} title="WhatsApp" text={text}
      notice={q.data && !q.data.available ? (
        <div className="notice" role="status">
          <span className="pill info">{t("In Kürze verfügbar", "Coming soon")}</span>{" "}
          {t("Der Versand per WhatsApp startet in Kürze. Ihr verbindet dann eure eigene WhatsApp-Business-Nummer, und Meta rechnet die Nachrichten direkt mit euch ab. Bis dahin könnt ihr alles einstellen; der Probelauf zeigt im Postausgang, was verschickt würde.",
            "WhatsApp sending starts soon. You will then connect your own WhatsApp Business number, and Meta bills the messages directly to you. Until then you can set everything up; the dry run shows in the outbox what would be sent.")}
        </div>
      ) : undefined}>
      {q.error ? <ErrorState error={q.error} retry={() => void q.refetch()} /> : q.isPending ? <Loading /> : <Inhalt orgId={orgId} orgName={orgName} d={q.data} />}
    </SettingsFrame>
  );
}

function Inhalt({ orgId, orgName, d }: { orgId: string; orgName: string; d: WhatsappOverview }) {
  const { busy, error, run } = useAction();
  const lang = currentLang() === "en" ? "en" : "de";
  const gesperrt = !d.included || !!busy;
  const liveMoeglich = d.available && d.account?.status === "connected";
  return (
    <>
      {error && <p className="tk-error" role="alert">{error}</p>}
      {/* K-374: derselbe Wortlaut wie an allen gesperrten Modulen („Im Tarif Agentur enthalten · Upgrade auf Agentur“) */}
      {!d.included && <TarifHinweis modul="module_whatsapp" id="wa-tarif" was={t("WhatsApp-Hinweise", "WhatsApp notices")} />}

      <section className="panel stack" aria-labelledby="wa-nummer">
        <h2 id="wa-nummer">{t("Nummer und Versand", "Number and sending")}</h2>
        <div className="grid">
          <Nummer orgId={orgId} d={d} busy={!!busy} run={run} />
          <label>
            {t("Modus", "Mode")}
            <select value={d.mode} disabled={gesperrt} aria-describedby="wa-modus-hinweis"
              onChange={(e) => void run("modus", () => saveWhatsappMode(api, orgId, e.target.value as "off" | "dry_run" | "live"), t("WhatsApp-Modus gespeichert.", "WhatsApp mode saved."))}>
              <option value="off">{t("Aus", "Off")}</option>
              <option value="dry_run">{t("Probelauf (nur protokollieren)", "Dry run (log only)")}</option>
              <option value="live" disabled={!liveMoeglich && d.mode !== "live"}>{t("Aktiv", "Active")}{liveMoeglich ? "" : !d.available ? t(" (in Kürze)", " (coming soon)") : t(" (erst Nummer verbinden)", " (connect a number first)")}</option>
            </select>
            <small id="wa-modus-hinweis">{d.mode === "dry_run"
              ? t(`Probelauf: ${d.dry_run_30d} Nachrichten in den letzten 30 Tagen protokolliert, nichts verschickt.`, `Dry run: ${d.dry_run_30d} messages logged in the last 30 days, nothing sent.`)
              : d.mode === "live" ? t("Nachrichten gehen an Creator mit Nummer und Einwilligung.", "Messages go to creators with a number and consent.")
              : t("Aus: es werden keine WhatsApp-Nachrichten vorbereitet.", "Off: no WhatsApp messages are prepared.")}
              {d.mode === "dry_run" && <> <Link to="/agentur/postausgang">{t("Zum Postausgang", "To the outbox")}</Link></>}</small>
          </label>
        </div>
        <p>
          <strong className="num">{d.creators_reachable}</strong>{t(` von ${d.creators_total} Creator haben eine Nummer hinterlegt und WhatsApp-Hinweisen zugestimmt.`, ` of ${d.creators_total} creators have stored a number and agreed to WhatsApp notices.`)}{" "}
          <small>{t("Creator tragen beides selbst in ihrem Profil ein und können die Einwilligung jederzeit zurücknehmen.", "Creators enter both themselves in their profile and can withdraw consent at any time.")}</small>
        </p>
      </section>

      <section className="panel stack" aria-labelledby="wa-ereignisse">
        <h2 id="wa-ereignisse">{t("Wann eine Nachricht rausgeht", "When a message goes out")}</h2>
        <p>{t("Vorausgewählt sind die Ereignisse, bei denen Creator etwas tun müssen. Jede Nachricht nennt eure Agentur und führt ins Creator-Portal.",
          "Preselected are the events where creators need to act. Every message names your agency and leads into the creator portal.")}</p>
        <div className="tablewrap tk-wa-wrap">
          <table className="tk-wa-ereignisse">
            <caption className="sr-only">{t("WhatsApp je Ereignis", "WhatsApp per event")}</caption>
            <thead><tr>
              <th scope="col">{t("Ereignis", "Event")}</th><th scope="col">{t("Nachricht (Beispiel)", "Message (example)")}</th>{d.account && <th scope="col">{t("Vorlage bei Meta", "Template at Meta")}</th>}<th scope="col">WhatsApp</th>
            </tr></thead>
            <tbody>
              {d.events.map((e) => (
                <tr key={e.key}>
                  <td>
                    <strong>{whatsappEventLabel(e.key)}</strong>
                    {e.custom && e.enabled !== e.default && <small>{e.default ? t("Vorgabe: an", "Default: on") : t("Vorgabe: aus", "Default: off")}</small>}
                    <small className="tk-wa-schmal" aria-hidden="true">{whatsappVorschau(e.template, orgName, lang)}</small>
                  </td>
                  <td><small>{whatsappVorschau(e.template, orgName, lang)}</small><small className="muted">{whatsappFuss(lang)} · [{whatsappKnopf(lang)}]</small></td>
                  {d.account && (
                    <td title={vorlagenDetail(d.account, e.template)}>
                      <span className="row tk-gap-sm">
                        <VorlagePill stand={vorlagenStand(d.account.templates, e.template)} />
                        <KategoriePill kategorie={vorlagenKategorie(d.account.template_categories, e.template)} />
                      </span>
                    </td>
                  )}
                  <td>
                    <label className="row">
                      <input type="checkbox" checked={e.enabled} disabled={gesperrt} aria-label={t(`WhatsApp bei „${whatsappEventLabel(e.key)}“`, `WhatsApp for “${whatsappEventLabel(e.key)}”`)}
                        onChange={(ev) => void run(`ereignis:${e.key}`, () => setWhatsappEvent(api, orgId, e.key, ev.target.checked === e.default ? null : ev.target.checked),
                          ev.target.checked ? t("WhatsApp für dieses Ereignis an.", "WhatsApp on for this event.") : t("WhatsApp für dieses Ereignis aus.", "WhatsApp off for this event."))} />
                      <span>{e.enabled ? t("an", "on") : t("aus", "off")}</span>
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel stack" aria-labelledby="wa-kosten">
        <h2 id="wa-kosten">{t("Kosten und Datenschutz", "Costs and privacy")}</h2>
        <p>{t("Die Nachrichten laufen über die offizielle WhatsApp-Business-Plattform von Meta. Meta berechnet sie direkt mit eurem eigenen Meta-Konto nach seiner Preisliste; CreatorOffice schlägt nichts auf. Eine Nachricht geht nur an Creator, die ihre Nummer selbst hinterlegt und zugestimmt haben, und nur von eurer Agentur.",
          "Messages run through Meta's official WhatsApp Business Platform. Meta bills them directly to your own Meta account according to its price list; CreatorOffice adds no markup. A message only goes to creators who stored their number themselves and agreed, and only from your agency.")}</p>
      </section>
    </>
  );
}

function VorlagePill({ stand }: { stand: ReturnType<typeof vorlagenStand> }) {
  if (stand === "approved") return <span className="pill success">{t("geprüft", "approved")}</span>;
  if (stand === "rejected") return <span className="pill error">{t("abgelehnt", "rejected")}</span>;
  if (stand === "missing") return <span className="pill">{t("fehlt", "missing")}</span>;
  return <span className="pill info">{t("in Prüfung", "in review")}</span>;
}

/** Kategorie bei Meta (0180): Marketing ist teurer und unterliegt bei WhatsApp eigenen Grenzen je Empfänger */
function KategoriePill({ kategorie }: { kategorie: ReturnType<typeof vorlagenKategorie> }) {
  if (kategorie === "marketing") {
    return <span className="pill warning" title={t("Meta hat die Vorlage als Marketing eingestuft: höherer Preis je Nachricht, und WhatsApp begrenzt Marketing-Nachrichten je Empfänger.", "Meta classified the template as marketing: higher price per message, and WhatsApp limits marketing messages per recipient.")}>{t("Marketing", "Marketing")}</span>;
  }
  if (kategorie === "utility") return <span className="pill neutral">{t("Service", "Utility")}</span>;
  if (kategorie === "authentication") return <span className="pill neutral">{t("Anmeldung", "Authentication")}</span>;
  if (kategorie === "other") return <span className="pill neutral">{t("gemischt", "mixed")}</span>;
  return null;
}

/** Eigene Nummer: verbinden (Embedded Signup), Stand, trennen (0167) */
function Nummer({ orgId, d, busy, run }: { orgId: string; d: WhatsappOverview; busy: boolean; run: ReturnType<typeof useAction>["run"] }) {
  const a = d.account;
  const [trennen, setTrennen] = useState(false);
  const konfiguriert = !!whatsappSignupConfig() && !!api.whatsappSignup;
  const kannVerbinden = d.included && d.available && konfiguriert && !busy;
  const verbinden = () => void run("verbinden", async () => {
    const r = await runEmbeddedSignup();
    if (!r) throw new Error(t("Die Anmeldung bei Meta wurde abgebrochen. Es wurde nichts verbunden.", "The Meta sign-in was cancelled. Nothing was connected."));
    await api.whatsappSignup!({ org: orgId, ...r });
  }, t("WhatsApp-Nummer verbunden. Die Vorlagen sind bei Meta zur Prüfung eingereicht.", "WhatsApp number connected. The templates have been submitted to Meta for review."));
  if (a) {
    const n = Object.keys(a.templates ?? {}).length;
    const ok = Object.keys(a.templates ?? {}).filter((k) => vorlagenStand(a.templates, k) === "approved").length;
    const marketing = Object.keys(a.template_categories ?? {}).filter((k) => vorlagenKategorie(a.template_categories, k) === "marketing").length;
    return (
      <div className="stack">
        <strong>{t("Eure Nummer", "Your number")}</strong>
        <p>
          <span className="num">{a.display_phone ?? "–"}</span>{a.verified_name ? ` · ${a.verified_name}` : ""}{" "}
          {a.status === "connected" ? <span className="pill success">{t("verbunden", "connected")}</span> : <span className="pill error">{t("gestört", "problem")}</span>}
        </p>
        {a.status === "error" && <p className="tk-error" role="alert">{a.last_error ?? t("Meta hat den Zugang beendet. Bitte neu verbinden.", "Meta ended the access. Please reconnect.")}</p>}
        <small>{t(`${ok} von ${n} Vorlagen von Meta geprüft. Nachrichten gehen erst mit geprüfter Vorlage raus; das dauert meist nur Minuten.`, `${ok} of ${n} templates approved by Meta. Messages only go out with an approved template; this usually takes minutes.`)}</small>
        {marketing > 0 && <small>{t(`${marketing} ${marketing === 1 ? "Vorlage hat" : "Vorlagen hat"} Meta als Marketing eingestuft. Sie gehen weiter raus, kosten aber mehr; Einspruch ist im WhatsApp-Manager von Meta möglich.`, `Meta classified ${marketing} ${marketing === 1 ? "template" : "templates"} as marketing. They still go out but cost more; you can appeal in Meta's WhatsApp Manager.`)}</small>}
        <div className="row">
          {/* Vorlagen anlegen: fehlende oder beim Anlegen gescheiterte Vorlagen im eigenen WhatsApp-Konto der Agentur bei Meta einreichen */}
          {a.status === "connected" && konfiguriert && (
            <button type="button" className="btn" disabled={busy} onClick={() => void run("vorlagen", async () => {
              const r = await api.whatsappSignup!({ org: orgId, action: "vorlagen" });
              if (r.template_errors?.length) throw new Error(r.template_errors[0]);
              return r;
            }).then((r) => { if (r) toast(r.templates_created > 0
              ? t(`${r.templates_created} ${r.templates_created === 1 ? "Vorlage" : "Vorlagen"} bei Meta angelegt. Meta prüft sie, das dauert meist nur Minuten.`, `${r.templates_created} ${r.templates_created === 1 ? "template" : "templates"} created at Meta. Meta reviews them, usually within minutes.`)
              : t("Alle Vorlagen sind bei Meta angelegt. Der Stand ist aktualisiert.", "All templates exist at Meta. The status has been refreshed.")); })}>
              {busy ? t("Wird angelegt …", "Creating …") : t("Vorlagen bei Meta anlegen", "Create templates at Meta")}</button>
          )}
          {a.status === "error" && <button type="button" className="btn" disabled={!kannVerbinden} onClick={verbinden}>{t("Neu verbinden", "Reconnect")}</button>}
          <button type="button" className="btn" disabled={busy} onClick={() => setTrennen(true)}>{t("Trennen", "Disconnect")}</button>
        </div>
        <Confirm open={trennen} danger busy={busy} title={t("WhatsApp-Nummer trennen?", "Disconnect the WhatsApp number?")}
          text={t("Danach gehen keine WhatsApp-Hinweise mehr raus, bis ihr neu verbindet. Wartende Nachrichten werden nicht mehr verschickt; Mails und Glocke laufen weiter.", "No WhatsApp notices go out until you reconnect. Waiting messages are no longer sent; emails and the bell continue.")}
          confirmLabel={t("Trennen", "Disconnect")} onClose={() => setTrennen(false)}
          onConfirm={() => void run("trennen", () => disconnectWhatsapp(api, orgId), t("WhatsApp-Nummer getrennt.", "WhatsApp number disconnected.")).then(() => setTrennen(false))} />
      </div>
    );
  }
  return (
    <div className="stack">
      <strong>{t("Eure Nummer", "Your number")}</strong>
      <p>{t("Noch nicht verbunden. Ihr nutzt eure eigene WhatsApp-Business-Nummer; eure Creator sehen euren Namen als Absender und bekommen nie Nachrichten einer anderen Agentur.",
        "Not connected yet. You use your own WhatsApp Business number; your creators see your name as the sender and never receive messages from another agency.")}</p>
      <div className="row">
        <button type="button" className="btn" disabled={!kannVerbinden} onClick={verbinden}
          title={kannVerbinden ? undefined : t("In Kürze verfügbar", "Coming soon")}>{busy ? t("Wird verbunden …", "Connecting …") : t("Nummer verbinden", "Connect number")}</button>
        {!(d.available && konfiguriert) && <span className="pill info">{t("In Kürze", "Soon")}</span>}
      </div>
      {d.available && konfiguriert && <small>{t("Es öffnet sich die Anmeldung bei Meta. Dort wählt ihr euer Meta-Business-Konto und die Nummer; danach legt CreatorOffice die Nachrichtenvorlagen für euch an.", "The Meta sign-in opens. Choose your Meta business account and number there; CreatorOffice then creates the message templates for you.")}</small>}
    </div>
  );
}

